import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginPassword,
  login2FA,
  selectOrganization,
  logoutSession,
  apiLoginGoogle,
  apiImpersonateWithPin,
  apiSwitchOrganization,
} from "@/lib/api/auth";
import {
  clearOrgs,
  fetchUserOrganizations,
  setAmainvoiceLink,
  setBillbeeLink,
  setCompanyName,
  setLogoUrl,
  setOrganizationCreationDate,
  setOwnerName,
  setReplycoLink,
  setVentoryOneLink,
} from "@/data/slices/orgSlice";
import { clearConnection } from "@/lib/signalRClient";
import { getAccessTokenOrRefresh } from "@/lib/tokenFactory";
import eventBus from "@/lib/eventBus";
import {
  notifyError,
  dismissToast,
  notifyInfo,
  notifySuccess,
} from "@/lib/notify.jsx";
import i18n from "i18next";
import { endCurrentImpersonation, getMe } from "@/lib/api/account";
import { clearTabs } from "./tabsSlice";

const IMPERSONATION_ADMIN_REFRESH_TOKEN_KEY =
  "impersonation_admin_refresh_token";
const IMPERSONATION_ACTIVE_KEY = "is_impersonating";
const IMPERSONATION_ACTIVE_VALUE = "1";
const IMPERSONATION_TARGET_USER_ID_KEY = "impersonated_target_user_id";

const clearStoredImpersonationState = () => {
  localStorage.removeItem(IMPERSONATION_ADMIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATION_ACTIVE_KEY);
  localStorage.removeItem(IMPERSONATION_TARGET_USER_ID_KEY);
};

// --- Thunks ---

export const login = createAsyncThunk(
  "auth/login",
  async (
    { username, password, deviceHeaders },
    { dispatch, rejectWithValue, getState },
  ) => {
    const prevToastId = getState().auth.toastIds.login;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await loginPassword({
        username,
        password,
        deviceHeaders,
      });
      const { access_token, refresh_token, expires_in, authentication_stage } =
        res.data;

      const expiresAt = Date.now() + expires_in * 1000;

      if (authentication_stage === "authenticated") {
        clearStoredImpersonationState();
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("access_token_expires_at", expiresAt.toString());

        try {
          await dispatch(fetchUserInfo({ accessToken: access_token })).unwrap();
        } catch {
          return rejectWithValue({ error: "Failed to fetch user info" });
        }
      } else if (authentication_stage === "organization_selection") {
        dispatch(
          fetchUserOrganizations({ accessToken: access_token }),
        ).unwrap();
      }

      return {
        accessToken: access_token,
        expiresAt,
        stage: authentication_stage,
      };
    } catch (err) {
      const rawError = err?.response?.data?.error;
      const rawDesc =
        err?.response?.data?.error_description ||
        "pages.auth_settings.login.login_failed";

      if (rawError === "account_inactive") {
        const toastId = notifyInfo(rawDesc, { duration: 8000 });
        return rejectWithValue({ error: rawDesc, toastId, severity: "info" });
      }

      const toastId = notifyError(rawDesc, { duration: 8000 });
      return rejectWithValue({ error: rawDesc, toastId });
    }
  },
);

export const loginGoogle = createAsyncThunk(
  "auth/loginGoogle",
  async (
    { code, code_verifier, redirect_uri, deviceHeaders },
    { dispatch, rejectWithValue, getState },
  ) => {
    const prevToastId = getState().auth.toastIds.google;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await apiLoginGoogle({
        code,
        code_verifier,
        redirect_uri,
        deviceHeaders,
      });
      const { access_token, refresh_token, expires_in, authentication_stage } =
        res.data;
      const expiresAt = Date.now() + expires_in * 1000;

      if (authentication_stage === "authenticated") {
        clearStoredImpersonationState();
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("access_token_expires_at", expiresAt.toString());

        try {
          await dispatch(fetchUserInfo({ accessToken: access_token })).unwrap();
        } catch {
          return rejectWithValue({ error: "Failed to fetch user info" });
        }
      } else if (authentication_stage === "organization_selection") {
        dispatch(
          fetchUserOrganizations({ accessToken: access_token }),
        ).unwrap();
      }

      return {
        accessToken: access_token,
        expiresAt,
        stage: authentication_stage,
      };
    } catch (err) {
      const rawError = err?.response?.data?.error;
      const rawDesc = err?.response?.data?.error_description;

      if (rawError === "account_inactive") {
        const desc = rawDesc || "pages.auth_settings.login.login_failed";
        const toastId = notifyInfo(desc, { duration: 8000 });
        return rejectWithValue({ error: desc, toastId, severity: "info" });
      }

      if (rawError === "user_not_registered" && rawDesc) {
        let extra = {};
        try {
          const googleInfo = JSON.parse(rawDesc);
          extra = {
            googleRegistrationInfo: googleInfo,
            googleRegistrationPending: true,
          };
        } catch {
          // Ignore malformed Google registration payloads.
        }

        return rejectWithValue({
          error: rawDesc || "pages.auth_settings.login.login_failed",
          googleInfo: extra,
        });
      }

      const desc = rawDesc || "pages.auth_settings.login.login_failed";
      const toastId = notifyError(desc, { duration: 8000 });
      return rejectWithValue({ error: desc, toastId });
    }
  },
);

export const send2FA = createAsyncThunk(
  "auth/send2FA",
  async (
    { code, deviceHeaders },
    { dispatch, rejectWithValue, getState },
  ) => {
    const prevToastId = getState().auth.toastIds.send2FA;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await login2FA({
        code,
        deviceHeaders,
      });
      const { access_token, refresh_token, expires_in, authentication_stage } =
        res.data;

      const expiresAt = Date.now() + expires_in * 1000;

      if (authentication_stage === "authenticated") {
        clearStoredImpersonationState();
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("access_token_expires_at", expiresAt.toString());

        try {
          await dispatch(fetchUserInfo({ accessToken: access_token })).unwrap();
        } catch {
          return rejectWithValue({ error: "Failed to fetch user info" });
        }
      } else if (authentication_stage === "organization_selection") {
        dispatch(
          fetchUserOrganizations({ accessToken: access_token }),
        ).unwrap();
      }

      return {
        accessToken: access_token,
        expiresAt,
        stage: authentication_stage,
      };
    } catch (err) {
      const description =
        err?.response?.data?.error_description ||
        "pages.auth_settings.login.2fa_failed";
      const toastId = notifyError(description, { duration: 8000 });
      return rejectWithValue({ error: description, toastId });
    }
  },
);

export const chooseOrganization = createAsyncThunk(
  "auth/chooseOrganization",
  async ({ orgId, deviceHeaders }, { dispatch, rejectWithValue, getState }) => {
    const prevToastId = getState().auth.toastIds.chooseOrganization;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await selectOrganization({
        orgId,
        deviceHeaders,
      });
      const { access_token, refresh_token, expires_in, authentication_stage } =
        res.data;

      const expiresAt = Date.now() + expires_in * 1000;

      if (authentication_stage === "authenticated") {
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("access_token_expires_at", expiresAt.toString());

        try {
          await dispatch(fetchUserInfo({ accessToken: access_token })).unwrap();
        } catch {
          return rejectWithValue({ error: "Failed to fetch user info" });
        }
      }

      return {
        accessToken: access_token,
        expiresAt,
        stage: authentication_stage,
      };
    } catch (err) {
      const description =
        err?.response?.data?.error_description ||
        "pages.auth_settings.login.organization_selection_failed";
      const toastId = notifyError(description, { duration: 8000 });
      return rejectWithValue({ error: description, toastId });
    }
  },
);

export const switchOrganization = createAsyncThunk(
  "auth/switchOrganization",
  async ({ orgId, deviceHeaders }, { dispatch, rejectWithValue, getState }) => {
    const state = getState();
    const prevToastId = state.auth.toastIds.switchOrganization;
    if (prevToastId) dismissToast(prevToastId);

    if (state.auth.isImpersonated) {
      return rejectWithValue({
        error:
          "components.organization_switch.backend.impersonated_session_not_allowed",
      });
    }

    if (state.auth.currentOrg === orgId) {
      return rejectWithValue({
        error: "components.organization_switch.state.error_same_organization",
      });
    }

    try {
      const accessToken = await getAccessTokenOrRefresh();
      if (!accessToken) {
        return rejectWithValue({
          error:
            "components.organization_switch.backend.authenticated_token_required",
        });
      }

      const res = await apiSwitchOrganization({
        orgId,
        accessToken,
        deviceHeaders,
      });

      const { access_token, refresh_token, expires_in, authentication_stage } =
        res.data;

      if (authentication_stage !== "authenticated") {
        return rejectWithValue({
          error: "components.organization_switch.state.error_switch_failed",
        });
      }

      const expiresAt = Date.now() + expires_in * 1000;

      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("access_token_expires_at", expiresAt.toString());

      await clearConnection();
      dispatch(clearTabs());
      dispatch(clearOrgs());

      try {
        await dispatch(fetchUserInfo({ accessToken: access_token })).unwrap();
      } catch {
        return rejectWithValue({
          error: "pages.auth_settings.login.fetch_user_info_failed",
        });
      }

      notifySuccess("pages.organizations.list.switch_success");

      return {
        accessToken: access_token,
        expiresAt,
        stage: authentication_stage,
      };
    } catch (err) {
      const description =
        err?.response?.data?.error_description ||
        "components.organization_switch.state.error_switch_failed";
      const toastId = notifyError(description, { duration: 8000 });
      return rejectWithValue({ error: description, toastId });
    }
  },
);

export const startImpersonation = createAsyncThunk(
  "auth/startImpersonation",
  async (
    { targetUserId, pin, deviceHeaders },
    { dispatch, rejectWithValue, getState },
  ) => {
    const state = getState();

    if (state.auth.isImpersonated) {
      return rejectWithValue({
        error: "components.impersonation.state.error_already_impersonating",
      });
    }

    const adminRefreshToken = localStorage.getItem("refresh_token");
    if (!adminRefreshToken) {
      return rejectWithValue({
        error:
          "components.impersonation.state.error_missing_admin_refresh_token",
      });
    }

    try {
      const adminAccessToken = await getAccessTokenOrRefresh();
      if (!adminAccessToken) {
        return rejectWithValue({
          error:
            "components.impersonation.state.error_missing_admin_access_token",
        });
      }

      const res = await apiImpersonateWithPin({
        targetUserId,
        pin: pin.trim().toUpperCase(),
        accessToken: adminAccessToken,
        deviceHeaders,
      });

      const { refresh_token, expires_in, authentication_stage } = res.data;

      if (authentication_stage !== "authenticated") {
        return rejectWithValue({
          error:
            "components.impersonation.state.error_not_authenticated_session",
        });
      }

      const expiresAt = Date.now() + expires_in * 1000;

      localStorage.setItem(
        IMPERSONATION_ADMIN_REFRESH_TOKEN_KEY,
        adminRefreshToken,
      );
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("access_token_expires_at", expiresAt.toString());
      localStorage.setItem(
        IMPERSONATION_ACTIVE_KEY,
        IMPERSONATION_ACTIVE_VALUE,
      );
      localStorage.setItem(
        IMPERSONATION_TARGET_USER_ID_KEY,
        targetUserId.toString(),
      );

      dispatch(clearTabs());
      dispatch(clearOrgs());
      dispatch(clearAuth());
      clearConnection();

      // window.history.replaceState({}, "", window.location.pathname);
      notifySuccess("components.impersonation.admin_card.success_started");

      return true;
    } catch (err) {
      const description =
        err?.response?.data?.error_description ||
        "components.impersonation.state.error_start_failed";
      return rejectWithValue({ error: description });
    }
  },
);

export const exitImpersonation = createAsyncThunk(
  "auth/exitImpersonation",
  async (_, { dispatch, rejectWithValue }) => {
    const originalAdminRefreshToken = localStorage.getItem(
      IMPERSONATION_ADMIN_REFRESH_TOKEN_KEY,
    );

    if (!originalAdminRefreshToken) {
      return rejectWithValue({
        error:
          "components.impersonation.state.error_missing_original_admin_session",
      });
    }

    try {
      await endCurrentImpersonation();
    } catch (err) {
      return rejectWithValue({
        error:
          err?.response?.data?.error_description ||
          "components.impersonation.finish.error",
      });
    }

    localStorage.setItem("refresh_token", originalAdminRefreshToken);
    localStorage.removeItem("access_token_expires_at");
    clearStoredImpersonationState();

    dispatch(clearTabs());
    dispatch(clearOrgs());
    dispatch(clearAuth());
    clearConnection();

    // window.history.replaceState({}, "", window.location.pathname);

    notifySuccess("components.impersonation.finish.success");

    return true;
  },
);

export const restoreSession = createAsyncThunk(
  "auth/restoreSession",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = await getAccessTokenOrRefresh();
      if (!token) throw new Error("No access token");

      try {
        await dispatch(fetchUserInfo({ accessToken: token })).unwrap();
      } catch {
        return rejectWithValue({ error: "Failed to fetch user info" });
      }

      return {
        stage: "authenticated",
      };
    } catch {
      return rejectWithValue("Session restore failed");
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch, getState }) => {
    const state = getState();
    const accessToken = state.auth.accessToken;
    const refreshToken = localStorage.getItem("refresh_token");
    const sessionToken = refreshToken || accessToken;
    if (!sessionToken) throw new Error("No session token");

    await clearConnection();
    await logoutSession(sessionToken);

    localStorage.removeItem("refresh_token");
    localStorage.removeItem("access_token_expires_at");
    clearStoredImpersonationState();

    dispatch(clearOrgs());
    dispatch(clearAuth());
    eventBus.emit("ForceLogout");
  },
);

export const handleForcedLogout = createAsyncThunk(
  "auth/handleForcedLogout",
  async (_, { dispatch }) => {
    const originalAdminRefreshToken = localStorage.getItem(
      IMPERSONATION_ADMIN_REFRESH_TOKEN_KEY,
    );
    const isImpersonating =
      localStorage.getItem(IMPERSONATION_ACTIVE_KEY) ===
      IMPERSONATION_ACTIVE_VALUE;

    const restoreImpersonator =
      isImpersonating && Boolean(originalAdminRefreshToken);
    if (restoreImpersonator) {
      localStorage.setItem("refresh_token", originalAdminRefreshToken);
    } else {
      localStorage.removeItem("refresh_token");
    }
    localStorage.removeItem("access_token_expires_at");
    clearStoredImpersonationState();

    dispatch(clearTabs());
    dispatch(clearOrgs());
    dispatch(clearAuth());
    clearConnection();
    if (!restoreImpersonator) eventBus.emit("ForceLogout");

    return restoreImpersonator;
  },
);

export const fetchUserInfo = createAsyncThunk(
  "auth/fetchUserInfo",
  async ({ accessToken }, { dispatch, rejectWithValue, getState }) => {
    const prevToastId = getState().auth.toastIds.fetchUserInfo;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await getMe(accessToken);
      const {
        userId,
        roles,
        organizationId,
        organizationRole,
        suggestFwoFactor,
        openCookieDialog,
        acceptedEssentialCookies,
        acceptedAnalyticsCookies,
        acceptedEngagementCookies,
        twoFactorEnabled,
        email,
        firstName,
        lastName,
        userCreationDate,
        avatarUrl,
        languageCode,
        permissions,
        logoUrl,
        companyName,
        organizationCreationDate,
        ownerFirstName,
        ownerLastName,
        passwordPolicy,
        amainvoiceLink,
        billbeeLink,
        replycoLink,
        ventoryOneLink,
        helpScout = null,
        guruu = null,
        refiner = null,
        isImpersonated = false,
        impersonatedByUserId = null,
      } = res.data;

      dispatch(setLogoUrl(logoUrl ?? null));
      dispatch(setCompanyName(companyName ?? null));
      dispatch(setOrganizationCreationDate(organizationCreationDate ?? null));

      if (ownerFirstName && ownerLastName) {
        dispatch(
          setOwnerName({ firstName: ownerFirstName, lastName: ownerLastName }),
        );
      }

      dispatch(setAmainvoiceLink(amainvoiceLink ?? null));
      dispatch(setBillbeeLink(billbeeLink ?? null));
      dispatch(setReplycoLink(replycoLink ?? null));
      dispatch(setVentoryOneLink(ventoryOneLink ?? null));

      if (languageCode) {
        i18n.changeLanguage(languageCode);
      }

      const isInternalUser =
        roles.includes("SuperAdmin") ||
        roles.includes("OrdinaryAdmin") ||
        roles.includes("Supporter") ||
        roles.includes("Accounter") ||
        roles.includes("MarketplaceEditor") ||
        roles.includes("MarketplacePartner") ||
        roles.includes("NewsEditor");

      if (!isInternalUser && !organizationId) {
        dispatch(fetchUserOrganizations({ accessToken }));
      }

      return {
        userId,
        roles,
        currentOrg: organizationId,
        orgRole: organizationRole,
        suggestFwoFactor,
        openCookieDialog,
        acceptedEssentialCookies,
        acceptedAnalyticsCookies,
        acceptedEngagementCookies,
        twoFactorEnabled,
        email,
        firstName,
        lastName,
        userCreationDate,
        avatarUrl,
        permissions,
        passwordPolicy,
        helpScout,
        guruu,
        refiner,
        isImpersonated,
        impersonatedByUserId,
      };
    } catch {
      const auth = getState().auth;
      if (
        auth.loggingOut ||
        (!auth.accessToken && !localStorage.getItem("refresh_token"))
      ) {
        return rejectWithValue();
      }

      const description = "pages.auth_settings.login.fetch_user_info_failed";
      const toastId = notifyError(description, { duration: 8000 });
      return rejectWithValue({ error: description, toastId });
    }
  },
);

// --- Slice ---

const initialState = {
  accessToken: null,
  expiresAt: null,
  userId: null,
  roles: [],
  currentOrg: null,
  orgRole: null,
  loading: false,
  loggingOut: false,
  restoringSession: false,
  identitySwitching: false,
  error: null,
  suggestFwoFactor: false,
  openCookieDialog: false,
  acceptedEssentialCookies: null,
  acceptedAnalyticsCookies: null,
  acceptedEngagementCookies: null,
  twoFactorEnabled: false,
  passwordPolicy: {
    requiresChange: false,
    changeReason: null,
  },
  email: null,
  firstName: null,
  lastName: null,
  userCreationDate: null,
  avatarUrl: null,
  permissions: [],
  helpScout: null,
  guruu: null,
  refiner: null,
  isImpersonated: false,
  impersonatedByUserId: null,
  toastIds: {
    login: null,
    google: null,
    send2FA: null,
    chooseOrganization: null,
    switchOrganization: null,
    fetchUserInfo: null,
  },
  errors: {
    login: null,
    google: null,
    send2FA: null,
    chooseOrganization: null,
    switchOrganization: null,
    fetchUserInfo: null,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCurrentOrg(state, action) {
      state.currentOrg = action.payload.id;
      state.orgRole = action.payload.role;
    },
    clearAuth(state) {
      state.accessToken = null;
      state.stage = null;
      state.userId = null;
      state.expiresAt = null;
      state.roles = [];
      state.currentOrg = null;
      state.orgRole = null;
      state.loading = false;
      state.restoringSession = false;
      state.identitySwitching = false;
      state.error = null;
      state.suggestFwoFactor = false;
      state.openCookieDialog = false;
      state.acceptedEssentialCookies = null;
      state.acceptedAnalyticsCookies = null;
      state.acceptedEngagementCookies = null;
      state.twoFactorEnabled = false;
      state.passwordPolicy = {
        requiresChange: false,
        changeReason: null,
      };
      state.email = null;
      state.firstName = null;
      state.lastName = null;
      state.userCreationDate = null;
      state.avatarUrl = null;
      state.permissions = [];
      state.helpScout = null;
      state.guruu = null;
      state.refiner = null;
      state.isImpersonated = false;
      state.impersonatedByUserId = null;
      state.toastIds = {
        google: null,
        login: null,
        send2FA: null,
        chooseOrganization: null,
        switchOrganization: null,
        fetchUserInfo: null,
      };
      state.errors = {
        login: null,
        google: null,
        send2FA: null,
        chooseOrganization: null,
        switchOrganization: null,
        fetchUserInfo: null,
      };
    },
    updateToken(state, action) {
      state.accessToken = action.payload.accessToken;
      state.expiresAt = action.payload.expiresAt;
    },
    clearSuggestFwoFactor(state) {
      state.suggestFwoFactor = false;
    },
    clearOpenCookieDialog(state) {
      state.openCookieDialog = false;
    },
    updateCookieConsent(state, action) {
      state.acceptedEssentialCookies = action.payload.acceptedEssentialCookies;
      state.acceptedAnalyticsCookies = action.payload.acceptedAnalyticsCookies;
      state.acceptedEngagementCookies =
        action.payload.acceptedEngagementCookies;
      state.openCookieDialog = false;
    },
    enableTwoFactor(state) {
      state.twoFactorEnabled = true;
    },
    clearPasswordPolicy(state) {
      state.passwordPolicy = {
        requiresChange: false,
        changeReason: null,
      };
    },
    updateAvatarUrl(state, action) {
      state.avatarUrl = action.payload.avatarUrl;
    },
    updateUserName(state, action) {
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.errors.login = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
        state.errors.login = null;
        state.toastIds.login = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.errors.login = action.payload?.error || null;
        state.toastIds.login = action.payload?.toastId || null;
      })

      .addCase(loginGoogle.pending, (state) => {
        state.loading = true;
        state.errors.google = null;
      })
      .addCase(loginGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
        state.errors.google = null;
        state.toastIds.google = null;
      })
      .addCase(loginGoogle.rejected, (state, action) => {
        if (action.payload?.googleInfo) {
          const params = new URLSearchParams(
            action.payload?.googleInfo.googleRegistrationInfo,
          ).toString();
          window.location.href = `/registration?${params}`;
        } else {
          state.loading = false;
          state.errors.google = action.payload?.error || null;
          state.toastIds.google = action.payload?.toastId || null;
        }
      })

      .addCase(send2FA.pending, (state) => {
        state.loading = true;
        state.errors.send2FA = null;
      })
      .addCase(send2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
        state.errors.send2FA = null;
        state.toastIds.send2FA = null;
      })
      .addCase(send2FA.rejected, (state, action) => {
        state.loading = false;
        state.errors.send2FA = action.payload?.error || null;
        state.toastIds.send2FA = action.payload?.toastId || null;
      })

      .addCase(chooseOrganization.pending, (state) => {
        state.loading = true;
        state.errors.chooseOrganization = null;
      })
      .addCase(chooseOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
        state.errors.chooseOrganization = null;
        state.toastIds.chooseOrganization = null;
      })
      .addCase(chooseOrganization.rejected, (state, action) => {
        state.loading = false;
        state.errors.chooseOrganization = action.payload?.error || null;
        state.toastIds.chooseOrganization = action.payload?.toastId || null;
      })

      .addCase(switchOrganization.pending, (state) => {
        state.loading = true;
        state.identitySwitching = true;
        state.errors.switchOrganization = null;
      })
      .addCase(switchOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.identitySwitching = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
        state.errors.switchOrganization = null;
        state.toastIds.switchOrganization = null;
      })
      .addCase(switchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.identitySwitching = false;
        state.errors.switchOrganization = action.payload?.error || null;
        state.toastIds.switchOrganization = action.payload?.toastId || null;
      })

      .addCase(startImpersonation.pending, (state) => {
        state.loading = true;
        state.identitySwitching = true;
      })
      .addCase(startImpersonation.fulfilled, (state, action) => {
        state.loading = false;
        state.identitySwitching = false;
        state.accessToken = action.payload.accessToken;
        state.expiresAt = action.payload.expiresAt;
        state.stage = action.payload.stage || null;
      })
      .addCase(startImpersonation.rejected, (state) => {
        state.loading = false;
        state.identitySwitching = false;
      })

      .addCase(exitImpersonation.pending, (state) => {
        state.loading = true;
        state.identitySwitching = true;
      })
      .addCase(exitImpersonation.fulfilled, (state) => {
        state.loading = false;
        state.identitySwitching = false;
      })
      .addCase(exitImpersonation.rejected, (state) => {
        state.loading = false;
        state.identitySwitching = false;
      })

      .addCase(logout.pending, (state) => {
        state.loggingOut = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loggingOut = false;
      })
      .addCase(logout.rejected, (state) => {
        state.loggingOut = false;
      })

      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
        state.errors.fetchUserInfo = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.userId;
        state.roles = action.payload.roles;
        state.currentOrg = action.payload.currentOrg;
        state.orgRole = action.payload.orgRole;
        state.errors.fetchUserInfo = null;
        state.toastIds.fetchUserInfo = null;
        state.openCookieDialog = action.payload.openCookieDialog;
        state.suggestFwoFactor = action.payload.suggestFwoFactor;
        state.acceptedEssentialCookies =
          action.payload.acceptedEssentialCookies;
        state.acceptedAnalyticsCookies =
          action.payload.acceptedAnalyticsCookies;
        state.acceptedEngagementCookies =
          action.payload.acceptedEngagementCookies;
        state.twoFactorEnabled = action.payload.twoFactorEnabled;
        state.passwordPolicy = action.payload.passwordPolicy;
        state.email = action.payload.email;
        state.firstName = action.payload.firstName;
        state.lastName = action.payload.lastName;
        state.userCreationDate = action.payload.userCreationDate;
        state.avatarUrl = action.payload.avatarUrl;
        state.permissions = action.payload.permissions;
        state.helpScout = action.payload.helpScout ?? null;
        state.guruu = action.payload.guruu ?? null;
        state.refiner = action.payload.refiner ?? null;
        state.isImpersonated = action.payload.isImpersonated;
        state.impersonatedByUserId = action.payload.impersonatedByUserId;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.errors.fetchUserInfo = action.payload?.error || null;
        state.toastIds.fetchUserInfo = action.payload?.toastId || null;
      })

      .addCase(restoreSession.pending, (state) => {
        state.restoringSession = true;
        state.error = null;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.restoringSession = false;
        state.stage = action.payload.stage || null;
      })
      .addCase(restoreSession.rejected, (state) => {
        state.restoringSession = false;
      });
  },
});

export const {
  setCurrentOrg,
  clearAuth,
  updateToken,
  clearSuggestFwoFactor,
  clearOpenCookieDialog,
  updateCookieConsent,
  enableTwoFactor,
  clearPasswordPolicy,
  updateAvatarUrl,
  updateUserName,
} = authSlice.actions;

export default authSlice.reducer;
